// ─── KAELO API Client ─────────────────────────────────────────────────────────
// All backend API calls live here. Import named functions as needed.

import axios from 'axios';
import {
  Task, TaskColumn, Notification, RegistryItem, RegistryStatus,
  CalendarBlock, ChatMessage, Settings,
} from './types';

// Always use relative URLs (same-origin as the page) so the app works on any
// domain the preview is served from — UUID, custom slug, custom domain, or
// inside an iframe. This avoids CORS/credentialed-cookie issues entirely.
const BASE = '';

const api = axios.create({
  baseURL: BASE,
  withCredentials: true,
});

// ─── Token Refresh Interceptor ────────────────────────────────────────────────
// AuthContext registers these two callbacks after mount so the interceptor can
// clear user state and redirect without importing React hooks directly.

type NavigateFn = (path: string) => void;

let _navigate: NavigateFn | null = null;
let _setUser: ((u: null) => void) | null = null;
let _accessToken: string | null = null;

export function setAccessToken(token: string | null): void {
  _accessToken = token;
}

/** Called once by AuthProvider after useNavigate() is available. */
export function setNavigate(fn: NavigateFn): void {
  _navigate = fn;
}

/** Called once by AuthProvider to give the interceptor access to setUser. */
export function setAuthUser(fn: (u: null) => void): void {
  _setUser = fn;
}

let _isRefreshing = false;
let _refreshQueue: Array<(retry: boolean) => void> = [];

// Endpoints that return 401 for semantic reasons (bad credentials, invalid
// token, etc.) rather than "session expired". These must never trigger the
// silent-refresh cycle — doing so swallows the real error, fires a pointless
// refresh request, and unconditionally redirects to /login?reason=session-expired
// even when the user is already on the login page.
const AUTH_NO_REFRESH = new Set([
  '/api/auth/login',
  '/api/auth/refresh',         // also deadlock-guards the refresh endpoint itself
  '/api/auth/register',
  '/api/auth/verify-email',
  '/api/auth/accept-invite',
  '/api/auth/resend-verification',
]);

api.interceptors.request.use((config) => {
  if (_accessToken) {
    config.headers.Authorization = `Bearer ${_accessToken}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Skip the refresh cycle for endpoints where a 401 carries its own
    // meaning (wrong credentials, expired invite token, etc.).  Any endpoint
    // NOT in AUTH_NO_REFRESH (e.g. /api/auth/me, /api/tasks, …) will still
    // attempt a silent token refresh on 401 as intended.
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !AUTH_NO_REFRESH.has(originalRequest.url)
    ) {
      originalRequest._retry = true;

      if (_isRefreshing) {
        // Queue this request until the in-flight refresh resolves
        return new Promise((resolve, reject) => {
          _refreshQueue.push((success) => {
            if (success) resolve(api(originalRequest));
            else reject(error);
          });
        });
      }

      _isRefreshing = true;

      try {
        // httpOnly cookie carries the refresh token — no body needed
        const { data } = await api.post('/api/auth/refresh');

        // Store new access token from refresh response
        if (data?.access_token) {
          setAccessToken(data.access_token);
          localStorage.setItem('access_token', data.access_token);
        }

        // Drain queue: all waiting requests can now retry
        _refreshQueue.forEach((cb) => cb(true));
        _refreshQueue = [];

        return api(originalRequest);
      } catch (refreshError) {
        // Drain queue: all waiting requests fail
        _refreshQueue.forEach((cb) => cb(false));
        _refreshQueue = [];

        // Clear auth state and redirect
        _setUser?.(null);
        if (_navigate) {
          _navigate('/login?reason=session-expired');
        } else {
          // Fallback: if navigate callback not set, reload login page
          window.location.href = '/login?reason=session-expired';
        }

        return Promise.reject(refreshError);
      } finally {
        _isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);


// ─── Auth ─────────────────────────────────────────────────────────────────────

export function formatApiError(detail: unknown): string {
  if (detail == null) return 'Something went wrong.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e))).join(' ');
  return String(detail);
}

export async function apiLogin(email: string, password: string) {
  const { data } = await api.post('/api/auth/login', { email, password });
  if (data?.access_token) {
    setAccessToken(data.access_token);
    localStorage.setItem('access_token', data.access_token);
  }
  return data;
}

export async function apiLogout() {
  setAccessToken(null);
  localStorage.removeItem('access_token');
  await api.post('/api/auth/logout');
}

export async function apiMe() {
  const { data } = await api.get('/api/auth/me');
  return data;
}

// ─── Agents ───────────────────────────────────────────────────────────────────

export async function apiListAgents(): Promise<{ agents: any[]; source: string }> {
  const { data } = await api.get('/api/agents');
  return data;
}

export async function apiGetAgent(name: string): Promise<any> {
  const { data } = await api.get(`/api/agents/${encodeURIComponent(name)}`);
  return data;
}

// ─── Tasks ────────────────────────────────────────────────────────────────────

export async function apiListTasks(officeId?: string): Promise<Task[]> {
  const { data } = await api.get('/api/tasks', { params: officeId ? { office_id: officeId } : {} });
  return data;
}

export async function apiCreateTask(task: Partial<Task>): Promise<Task> {
  const { data } = await api.post('/api/tasks', task);
  return data;
}

export async function apiUpdateTask(id: string, update: Partial<Task>): Promise<Task> {
  const { data } = await api.put(`/api/tasks/${id}`, update);
  return data;
}

export async function apiDeleteTask(id: string): Promise<void> {
  await api.delete(`/api/tasks/${id}`);
}

// ─── Notifications ────────────────────────────────────────────────────────────

export async function apiListNotifications(): Promise<Notification[]> {
  const { data } = await api.get('/api/notifications');
  return data;
}

export async function apiUpdateNotification(id: string, status: string): Promise<Notification> {
  const { data } = await api.put(`/api/notifications/${id}`, { status });
  return data;
}

// ─── Registry ─────────────────────────────────────────────────────────────────

export async function apiListRegistry(): Promise<RegistryItem[]> {
  const { data } = await api.get('/api/registry');
  return data;
}

export async function apiCreateRegistryItem(item: Partial<RegistryItem>): Promise<RegistryItem> {
  const { data } = await api.post('/api/registry', item);
  return data;
}

export async function apiUpdateRegistryItem(id: string, update: Partial<RegistryItem>): Promise<RegistryItem> {
  const { data } = await api.put(`/api/registry/${id}`, update);
  return data;
}

export async function apiDeleteRegistryItem(id: string): Promise<void> {
  await api.delete(`/api/registry/${id}`);
}

// ─── Calendar ─────────────────────────────────────────────────────────────────

export async function apiListCalendar(): Promise<CalendarBlock[]> {
  const { data } = await api.get('/api/calendar');
  return data;
}

export async function apiCreateCalendarBlock(block: Partial<CalendarBlock>): Promise<CalendarBlock> {
  const { data } = await api.post('/api/calendar', block);
  return data;
}

export async function apiUpdateCalendarBlock(id: string, update: Partial<CalendarBlock>): Promise<CalendarBlock> {
  const { data } = await api.put(`/api/calendar/${id}`, update);
  return data;
}

export async function apiDeleteCalendarBlock(id: string): Promise<void> {
  await api.delete(`/api/calendar/${id}`);
}

// ─── Chat ─────────────────────────────────────────────────────────────────────

export async function apiChatSend(webhookUrl: string, agentName: string, message: string) {
  const { data } = await api.post('/api/chat/send', {
    webhook_url: webhookUrl,
    agent_name: agentName,
    message,
  });
  return data;
}

export async function apiChatPoll(pollUrl: string, lastMessageId?: string): Promise<{ messages: ChatMessage[] }> {
  const { data } = await api.get('/api/chat/poll', {
    params: { poll_url: pollUrl, last_message_id: lastMessageId },
  });
  return data;
}

export async function apiGetChatHistory(conversationId: string): Promise<{ messages: ChatMessage[] }> {
  const { data } = await api.get(`/api/chat/history/${conversationId}`);
  return data;
}

export async function apiAddChatMessage(conversationId: string, msg: ChatMessage): Promise<void> {
  await api.post(`/api/chat/history/${conversationId}/messages`, msg);
}

export async function apiClearChatHistory(conversationId: string): Promise<void> {
  await api.delete(`/api/chat/history/${conversationId}`);
}

// ─── Reports ──────────────────────────────────────────────────────────────────

export async function apiFetchReport(reportUrl: string): Promise<{ content: string }> {
  const { data } = await api.get('/api/reports/fetch', { params: { report_url: reportUrl } });
  return data;
}

// ─── Proxy Fetch (scaffold panel data) ───────────────────────────────────────

export async function apiProxyFetch(url: string): Promise<{ data: any; status: number }> {
  const { data } = await api.get('/api/proxy/fetch', { params: { url } });
  return data;
}

// ─── Agent Activity ───────────────────────────────────────────────────────────

export async function apiGetAgentActivity(agentName: string): Promise<Array<{ time: string; type: string; action: string }>> {
  const { data } = await api.get(`/api/agents/${encodeURIComponent(agentName)}/activity`);
  return data;
}

export default api;

// ─── Auth — Extended ──────────────────────────────────────────────────────────

// Registration: POST /api/auth/register
export const apiRegister = async (data: {
  email: string;
  password: string;
}): Promise<{ message: string }> => {
  const response = await api.post('/api/auth/register', data);
  return response.data;
};

// Email verification: POST /api/auth/verify-email
export const apiVerifyEmail = async (data: {
  token: string;
}): Promise<{ message: string }> => {
  const response = await api.post('/api/auth/verify-email', data);
  return response.data;
};

// Refresh token: POST /api/auth/refresh
export const apiRefresh = async (): Promise<{
  access_token: string;
  refresh_token: string;
  token_type: string;
}> => {
  const response = await api.post('/api/auth/refresh');
  return response.data;
};

// Send invite (admin): POST /api/auth/send-invite
export const apiSendInvite = async (data: {
  email: string;
  office_id: string;
}): Promise<{ message: string; invite_token: string }> => {
  const response = await api.post('/api/auth/send-invite', data);
  return response.data;
};

// Accept invite: POST /api/auth/accept-invite
export const apiAcceptInvite = async (data: {
  email: string;
  name: string;
  password: string;
  token: string;
}): Promise<{ message: string }> => {
  const response = await api.post('/api/auth/accept-invite', data);
  return response.data;
};

// Resend verification (for expired tokens): POST /api/auth/resend-verification
export const apiResendVerification = async (data?: {
  email: string;
}): Promise<{ message: string }> => {
  const response = await api.post('/api/auth/resend-verification', data);
  return response.data;
};
